import { amountToWordsEs } from "./numberToWordsEs";
import { prepareLogoForThermal } from "./ticketImage";

/** Ancho del iframe de impresión (px) ≈ 80 mm a 96 DPI. */
const PRINT_FRAME_WIDTH_PX = 384;

function esc(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function money(n) {
    return `$ ${Number(n).toFixed(2)}`;
}

function formatSaleDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "America/Mexico_City",
    });
}

function lineRule(char = "-", width = 42) {
    return char.repeat(width);
}

function rpad(str, len) {
    const s = String(str);
    return s.length >= len ? s.slice(0, len) : s + " ".repeat(len - s.length);
}

function lpad(str, len) {
    const s = String(str);
    return s.length >= len ? s.slice(-len) : " ".repeat(len - s.length) + s;
}

/** Líneas monoespaciadas para detalle (sin tabla HTML). */
function formatItemLines(li, unitDefault, width) {
    const desc = (li.name || "").toUpperCase();
    const unit = (li.unit || unitDefault || "Pza").slice(0, 4);
    const row2 =
        rpad(String(li.qty), 4) +
        rpad(unit, 5) +
        lpad(money(li.unit_price), 12) +
        lpad(money(li.line_total), 12);
    const lines = [desc.length > width ? desc.slice(0, width) : desc, row2.slice(0, width)];
    return lines.join("\n");
}

export async function prepareSettingsForPrint(settings) {
    const s = { ...(settings || {}) };
    const logo = (s.ticket_logo || "").trim();
    if (logo) {
        try {
            s.ticket_logo = await prepareLogoForThermal(logo);
        } catch {
            /* conservar logo original si falla el procesamiento */
        }
    }
    return s;
}

export function buildReceiptHtml(sale, settings) {
    const paperMm = Number(settings.ticket_paper_width_mm) || 80;
    const width = paperMm >= 80 ? 42 : 32;
    const biz = settings.business_name || "FERRETERÍA LOZMAR";
    const owner = settings.ticket_owner_name || "";
    const rfc = settings.ticket_rfc || "";
    const addr = settings.ticket_address || settings.address || "";
    const phones = settings.ticket_phones || settings.phone || "";
    const logo = (settings.ticket_logo || "").trim();
    const logoH = Math.min(120, Math.max(24, Number(settings.ticket_logo_height_px) || 72));
    const items = sale.items || [];
    const unitDefault = settings.ticket_unit_default || "Pza";

    const itemBlocks = items
        .map((li) => `<pre class="item-block">${esc(formatItemLines(li, unitDefault, width))}</pre>`)
        .join("");

    const detailHeader = `DESCRIPCION\n${rpad("CANT", 4)}${rpad("UNID", 5)}${lpad("PRECIO", 12)}${lpad("IMPORTE", 12)}`;

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Ticket ${esc(sale.ticket_number || "")}</title>
  <style>
    @page { size: ${paperMm}mm auto; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      width: ${paperMm - 8}mm;
      max-width: ${paperMm - 8}mm;
      font-family: "Courier New", Courier, monospace;
      font-size: 12px;
      line-height: 1.4;
      color: #000;
      font-weight: 600;
      padding: 4px;
      -webkit-font-smoothing: none;
    }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    .rule {
      text-align: center;
      margin: 6px 0;
      font-size: 11px;
      font-weight: 700;
      border: none;
      border-top: 1px dashed #000;
      padding-top: 4px;
    }
    .rule-solid { border-top-style: solid; }
    .meta { font-size: 11px; margin: 2px 0; font-weight: 600; }
    .pre, .item-block {
      font-family: inherit;
      font-size: 11px;
      font-weight: 700;
      white-space: pre;
      margin: 0;
      padding: 0;
      border: none;
      background: transparent;
      line-height: 1.35;
    }
    .head { margin-bottom: 4px; text-transform: uppercase; }
    .item-block { margin-bottom: 6px; }
    .total-row { font-size: 14px; font-weight: 700; text-align: right; margin: 8px 0; }
    .letters { font-size: 11px; margin: 8px 0; text-transform: uppercase; font-weight: 700; }
    .pay { text-align: right; font-size: 12px; margin: 4px 0; font-weight: 700; }
    .footer { font-size: 10px; text-align: center; margin-top: 10px; font-weight: 600; }
    .logo-wrap { text-align: center; margin-bottom: 8px; }
    .logo {
      display: block;
      margin: 0 auto;
      max-width: 100%;
      height: auto;
      object-fit: contain;
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
    }
    @media print {
      html, body {
        width: ${paperMm - 8}mm;
        margin: 0;
        padding: 2mm;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .rule { border-top: 1px dashed #000; }
    }
  </style>
</head>
<body>
  ${logo ? `<div class="logo-wrap"><img class="logo" src="${esc(logo)}" alt="" style="max-height:${logoH}px" /></div>` : ""}
  <div class="center bold" style="font-size:16px;margin-bottom:4px;">${esc(biz)}</div>
  ${owner ? `<div class="center meta">${esc(owner)}</div>` : ""}
  ${rfc ? `<div class="center meta">RFC ${esc(rfc)}</div>` : ""}
  ${addr ? `<div class="center meta">${esc(addr)}</div>` : ""}
  ${phones ? `<div class="center meta">${esc(phones)}</div>` : ""}

  <div class="meta" style="margin-top:10px;">${formatSaleDate(sale.created_at)}</div>
  <div class="meta">Ticket: <strong>${esc(sale.ticket_number || "—")}</strong></div>
  <div class="meta">Cajero: ${esc(sale.cashier_name || settings.ticket_cashier_default)}</div>
  <div class="meta">Caja: ${esc(sale.register_name || settings.ticket_register_name)}</div>
  <div class="meta">Cliente: ${esc(sale.customer_name || settings.ticket_customer_default)}</div>
  <div class="meta">Vendedor: ${esc(sale.seller_name || settings.ticket_seller_default)}</div>

  <div class="rule">- DETALLE DE SU COMPRA -</div>
  <pre class="pre head">${esc(detailHeader)}</pre>
  <div class="rule rule-solid">${lineRule("-", width)}</div>

  ${itemBlocks}

  <div class="rule rule-solid">${lineRule("=", width)}</div>
  <div class="total-row">TOTAL: ${money(sale.total)}</div>

  <div class="letters">IMPORTE CON LETRA: ${esc(amountToWordsEs(sale.total))}</div>

  <div class="rule">- FORMA DE PAGO -</div>
  <div class="pay">Efectivo: ${money(sale.amount_received)}</div>
  ${sale.change_given > 0 ? `<div class="pay">Cambio: ${money(sale.change_given)}</div>` : ""}

  <div class="footer">${esc(settings.ticket_footer_returns)}</div>
  <div class="footer bold" style="margin-top:8px;">${esc(settings.ticket_footer_thanks)}</div>
</body>
</html>`;
}

const PRINT_FRAME_ID = "lozmar-receipt-print-frame";

function getPrintFrame() {
    let iframe = document.getElementById(PRINT_FRAME_ID);
    if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = PRINT_FRAME_ID;
        iframe.setAttribute("title", "Impresión de ticket");
        iframe.style.cssText = [
            "position:fixed",
            "left:-9999px",
            "top:0",
            `width:${PRINT_FRAME_WIDTH_PX}px`,
            "height:900px",
            "border:0",
            "visibility:hidden",
            "pointer-events:none",
            "overflow:hidden",
        ].join(";");
        document.body.appendChild(iframe);
    }
    return iframe;
}

function runPrint(win) {
    setTimeout(() => {
        win.focus();
        win.print();
    }, 300);
}

/** Imprime vía iframe fuera de pantalla (evita ventana about:blank vacía). */
export async function printReceipt(sale, settings) {
    if (!sale) {
        throw new Error("No hay datos del ticket");
    }

    if (typeof document === "undefined" || !document.body) {
        throw new Error("Impresión no disponible en este entorno");
    }

    const prepared = await prepareSettingsForPrint(settings || {});
    const html = buildReceiptHtml(sale, prepared);

    const iframe = getPrintFrame();
    const win = iframe.contentWindow;
    if (!win) {
        return printReceiptViaBlob(html);
    }

    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();

    runPrint(win);
}

/** Respaldo: ventana con Blob (sin noopener para poder cargar el HTML). */
function printReceiptViaBlob(html) {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", `width=${PRINT_FRAME_WIDTH_PX},height=720`);
    if (!w) {
        URL.revokeObjectURL(url);
        throw new Error("Permite ventanas emergentes para imprimir el ticket");
    }
    const cleanup = () => {
        URL.revokeObjectURL(url);
        try {
            w.close();
        } catch (_e) {
            /* ignore */
        }
    };
    w.addEventListener("load", () => {
        setTimeout(() => {
            w.focus();
            w.print();
            setTimeout(cleanup, 600);
        }, 200);
    });
}
